import { useQuery } from '@tanstack/react-query';
import type { DayDrawView, ResultState } from '@lp/contracts';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { errorText, get } from '@/lib/api';
import { useT } from '@/lib/i18n';
import { Badge, Button, Empty, ErrorBox, Input, Loading, PageHeader } from '@/components/ui';

/** today in Cambodia, YYYY-MM-DD */
export function todayPP(): string {
  const p = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Phnom_Penh', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
  return p;
}
const shift = (date: string, n: number) => {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
};

export function ResultStateBadge({ state, revision }: { state: ResultState; revision?: number }) {
  const t = useT();
  if (state === 'published') return <Badge tone="green">{t('Published')}{revision && revision > 1 ? ` · r${revision}` : ''}</Badge>;
  if (state === 'draft') return <Badge tone="amber">{t('Draft')}</Badge>;
  return <Badge>{t('No result')}</Badge>;
}

/** every draw of a day across the products (Cambodia time), with its result state */
export function ResultsPage() {
  const t = useT();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [date, setDateState] = useState(params.get('date') ?? todayPP());
  const setDate = (d: string) => {
    setDateState(d);
    setParams({ date: d }, { replace: true });
  };
  const day = useQuery({ queryKey: ['results', date], queryFn: () => get<DayDrawView[]>(`/platform/results?date=${date}`) });
  const draws = day.data ?? [];
  const done = draws.filter((d) => d.state === 'published').length;
  const products = [...new Set(draws.map((d) => d.productCode))];

  return (
    <>
      <PageHeader
        title={t('Results')}
        subtitle={t('Enter each draw’s result, check it, publish it. Only a published result counts.')}
        actions={
          <div className="flex items-center gap-1">
            <Button variant="secondary" onClick={() => setDate(shift(date, -1))} aria-label={t('Previous day')}>
              <ChevronLeft size={15} />
            </Button>
            <Input type="date" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} className="w-40" aria-label={t('Date')} />
            <Button variant="secondary" onClick={() => setDate(shift(date, 1))} aria-label={t('Next day')}>
              <ChevronRight size={15} />
            </Button>
          </div>
        }
      />
      {day.isLoading ? (
        <Loading />
      ) : day.error ? (
        <ErrorBox text={errorText(day.error)} />
      ) : !draws.length ? (
        <Empty title={t('No draw on this day.')} />
      ) : (
        <>
          <p className="mb-3 text-sm text-ink-600">
            {done} / {draws.length} {t('published')}
          </p>
          <div className="space-y-3">
            {products.map((code) => (
              <section key={code} className="rounded-lg border border-ink-200 bg-white" data-testid={`results-${code}`}>
                <header className="border-b border-ink-100 px-4 py-2 text-sm font-semibold text-ink-900">
                  {draws.find((d) => d.productCode === code)!.productName} <span className="font-mono text-xs text-ink-500">{code}</span>
                </header>
                <div className="flex flex-wrap gap-2 p-3">
                  {draws
                    .filter((d) => d.productCode === code)
                    .map((d) => (
                      <button
                        key={`${d.gameCode}-${d.date}-${d.time}`}
                        onClick={() => nav(`/results/${d.productCode}/${d.gameCode}/${d.date}/${d.time}`)}
                        className="flex items-center gap-2 rounded-md border border-ink-200 px-2.5 py-1.5 text-sm hover:border-ink-300 hover:bg-ink-50"
                        data-testid={`draw-${d.productCode}-${d.gameCode}-${d.time}`}
                      >
                        <span className="font-mono font-semibold">{d.timePP}</span>
                        {d.gameCode !== d.productCode && <span className="font-mono text-xs text-ink-500">{d.gameCode}</span>}
                        <ResultStateBadge state={d.state} revision={d.revision} />
                      </button>
                    ))}
                </div>
              </section>
            ))}
          </div>
        </>
      )}
    </>
  );
}
